/**
 * "Needs attention" strip — turns the dense client dashboard into a decision-
 * oriented one. Pure render over buildPortfolioAlerts(); Quiet Authority styling
 * (hairline left rule by severity, no filled pills). Renders nothing when clean.
 */
import { buildPortfolioAlerts, type PortfolioAlert } from '../../../shared/portfolioAlerts';
import type { ClientWithCompliance, PortfolioSnapshot } from '../../../shared/api';

const SEVERITY_RULE: Record<PortfolioAlert['severity'], string> = {
  high: 'border-rust-deep',
  medium: 'border-rust',
  info: 'border-brass',
};
const SEVERITY_DOT: Record<PortfolioAlert['severity'], string> = {
  high: 'bg-rust-deep',
  medium: 'bg-rust',
  info: 'bg-brass',
};

const num = (v: string | number | null | undefined): number | undefined =>
  v == null || v === '' ? undefined : Number(v);

export function PortfolioAlertsStrip({
  client: c,
  portfolio,
  asOf,
}: {
  client: ClientWithCompliance;
  portfolio: PortfolioSnapshot;
  asOf?: string | null;
}) {
  const alerts = buildPortfolioAlerts({
    client: {
      reviewDue: c.reviewDue,
      targets: {
        equity_au: num(c.targetAllocAu),
        equity_intl: num(c.targetAllocIntl),
        property: num(c.targetAllocProperty),
        alt: num(c.targetAllocAlt),
        fi: num(c.targetAllocFi),
        cash: num(c.targetAllocCash),
      },
    },
    portfolio: {
      tnw: portfolio.tnw ?? 0,
      cash: portfolio.cash ?? 0,
      allocation: portfolio.allocation ?? [],
      holdings: (portfolio.accounts ?? []).flatMap((a) =>
        a.holdings.map((h) => ({ code: h.code, name: h.name, currentValue: h.currentValue })),
      ),
    },
    ...(asOf !== undefined ? { asOf } : {}),
  });

  if (alerts.length === 0) return null;

  return (
    <section className="lg:col-span-4 bg-white border border-hairline rounded shadow-sm overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5 border-b border-hairline flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-tight text-ink">Needs attention</span>
        <span className="text-[10px] qa-mono text-ink-mute">{alerts.length}</span>
      </div>
      <ul className="divide-y divide-hairline">
        {alerts.map((a) => (
          <li key={a.id} className={`flex items-start gap-3 pl-3 pr-3 py-2 border-l-2 ${SEVERITY_RULE[a.severity]}`}>
            <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${SEVERITY_DOT[a.severity]}`} />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-ink leading-tight">{a.title}</p>
              <p className="text-[11px] text-ink-mute leading-snug">{a.detail}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
